import axios, { AxiosPromise } from 'axios';
import { api, per_page as perPage, searchMicroserviceKey, searchMicroserviceUrl } from '../config/Master';
import { extractLocationData } from '../common';
import store from '../store';

const createUserProfile = (formData: any): AxiosPromise => {
  const { sex, interested, year, month, day } = formData;
  let { location } = formData;

  location = extractLocationData(location);

  return axios.post(`${api}/profile`, {
    sex,
    interested,
    year,
    month,
    day,
    ...location,
  });
};

const getLoggedProfile = (): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/auth/user?include=profile,photo,video`,
    data: {},
  });
};

const getProfileMapFields = (): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/map_fields/all`,
  });
};

const getSignedEmailPromoToken = (token: string): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/auth/validate-email-promo-token?email_promo_token=${token}`,
    data: {},
  });
};

const getLoggedAccount = (): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/auth/user?include=profile`,
    data: {},
  });
};

const updateProfileCoverData = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile/cover`,
    data: formData,
  });
};

const updateProfileOpeningOfferData = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile/opening_offer`,
    data: formData,
  });
};
const updateProfileAboutData = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile/about`,
    data: formData,
  });
};

const updateProfileInterestData = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile/interested_in`,
    data: formData,
  });
};

const updateProfileDescriptionData = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile/description`,
    data: formData,
  });
};

const updateProfileSeekingData = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile/seeking`,
    data: formData,
  });
};

const updateLoggedProfile = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/profile`,
    data: formData,
  });
};

const getAccountByUserName = (username: string): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/account/${username}`,
    data: {},
  });
};

const getExternalProfile = (username: string): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/account/pre/${username}?include=profile,offer,photo,video`,
    data: {},
  });
};

const getAccountSettings = (): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/settings?include=social,recurring`,
    data: {},
  });
};

const getVideoChatPreferences = (hash_id: number | string): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/settings/video-call/${hash_id}`,
    data: {},
  });
};

const getTurnCredentials = (): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/thread/video-call/turn-credential`,
  });
};

const getCheckInfoRequest = (): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/settings/check-info-request`,
    data: {},
  });
};

const requestUserInfo = (): AxiosPromise => {
  return axios({
    method: 'post',
    url: `${api}/settings/request-user-info`,
    data: {},
  });
};

const getBlockedList = (page: number): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/block?include=to_account,to_account.profile&page=${page}`,
    data: {},
  });
};

const getDismissedList = (page: number): AxiosPromise => {
  return axios({
    method: 'get',
    url: `${api}/dismiss?include=to_account,to_account.profile&page=${page}`,
    data: {},
  });
};

const editBiometricName = (newName: any, deviceId: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/webauthn/edit/${deviceId}`,
    data: {
      name: newName,
    },
  });
};

const updateAccountSettings = (formData: any): AxiosPromise => {
  return axios({
    method: 'put',
    url: `${api}/settings`,
    data: formData,
  });
};

const updateVideoChatPreferences = (hash_id: any, formData: any): AxiosPromise => {
  const videoFormData = {
    block_incoming: !!formData.block_incoming,
    have_conversed: !!formData?.have_conversed,
  };

  return axios({
    method: 'put',
    url: `${api}/settings/video-call/${hash_id}`,
    data: videoFormData,
  });
};

const gdprDelete = (formData: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/account/gdpr_delete`,
    data: formData,
  });
};

const gdprAcknowledged = (formData: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/account/gdpr_acknowledge`,
    data: formData,
  });
};

const mockProcessPhoto = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/photo-process`,
    data: formData,
  });
};

const postNewPhoto = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/photo`,
    data: formData,
  });
};

const uploadToS3 = (url: string, media: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: url,
    data: media,
    withCredentials: false,
  });
};

const getPhoto = (photoUrl: string): AxiosPromise => {
  return axios({
    method: 'GET',
    url: photoUrl,
    withCredentials: false,
  });
};

const processPhoto = (formData: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/photo-process`,
    data: formData,
  });
};

const deletePhoto = (photoId: any): AxiosPromise => {
  return axios({
    method: 'DELETE',
    url: `${api}/photo/${photoId}`,
    data: {},
  });
};

const putPhotoAvatar = (photoId: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/photo/avatar`,
    data: { id: photoId },
  });
};

const putPhotoPrivacy = (photoId: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/photo/${photoId}`,
    data: {},
  });
};

const reportUser = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/report`,
    data: formData,
  });
};

const getReportReasons = (): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/report/reasons/group`,
  });
};

const blockUser = (username: string): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/block/${username}`,
    data: {},
  });
};

const dismissUser = (username: string): AxiosPromise => {
  return axios({
    method: 'DELETE',
    url: `${api}/dismiss/${username}`,
    data: {},
  });
};

const getCredits = (per_page: number, page = 1): AxiosPromise => {
  const numberToGet = per_page === 0 ? perPage : per_page;

  return axios({
    method: 'GET',
    url: `${api}/credits?page=${page}&per_page=${numberToGet}`,
    data: {},
  });
};

const updateMandatoryCompletion = (formData: any): AxiosPromise => {
  return axios.post(`${api}/profile/mandatory-completion/update`, formData);
};

const getMandatoryCompletion = (): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/profile/mandatory-completion/validate`,
    data: {},
  });
};

const verifyPassword = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/account/verify-password`,
    data: formData,
  });
};

const updatePhoneNumber = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/account/change-phone-number`,
    data: formData,
  });
};

const updateProfileNewJoinFlow = (formData: any): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/profile/new-join-flow/update`,
    data: formData,
  });
};

const getSwipeModeAccounts = (): AxiosPromise => {
  if (store.getState().settings?.is_loom_discover_swipe_enable) {
    return axios({
      method: 'GET',
      url: `${searchMicroserviceUrl}/discover`,
      withCredentials: false,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${store.getState().auth.jwToken}`,
        'x-client-id': searchMicroserviceKey,
      },
    });
  }

  return axios({
    method: 'GET',
    url: `${api}/dashboard/swipe-mode/accounts?include=profile,photo,video`,
  });
};

const getQuickOfferUsers = (limit: number): AxiosPromise => {
  return axios({
    method: 'GET',
    url: `${api}/dashboard/swipe-mode/accounts?include=profile,photo,video&limit=${limit}`,
  });
};

const swipeModePassAccount = (toAccountHashId: string): AxiosPromise => {
  return axios({
    method: 'POST',
    url: `${api}/dashboard/swipe-mode/pass-account`,
    data: {
      to_account_hash_id: toAccountHashId,
    },
  });
};

const unlinkPhoneNumber = (formData: any): AxiosPromise => {
  return axios({
    method: 'PUT',
    url: `${api}/account/unlink-phone-number`,
    data: formData,
  });
};

export {
  createUserProfile,
  getLoggedProfile,
  getProfileMapFields,
  getSignedEmailPromoToken,
  getLoggedAccount,
  updateProfileCoverData,
  updateProfileAboutData,
  updateProfileInterestData,
  updateProfileDescriptionData,
  updateProfileSeekingData,
  updateLoggedProfile,
  getAccountByUserName,
  getExternalProfile,
  getAccountSettings,
  getVideoChatPreferences,
  getTurnCredentials,
  getCheckInfoRequest,
  requestUserInfo,
  getBlockedList,
  getDismissedList,
  editBiometricName,
  updateAccountSettings,
  updateVideoChatPreferences,
  gdprDelete,
  gdprAcknowledged,
  mockProcessPhoto,
  postNewPhoto,
  uploadToS3,
  getPhoto,
  processPhoto,
  deletePhoto,
  putPhotoAvatar,
  putPhotoPrivacy,
  reportUser,
  getReportReasons,
  blockUser,
  dismissUser,
  getCredits,
  updateMandatoryCompletion,
  getMandatoryCompletion,
  verifyPassword,
  updatePhoneNumber,
  updateProfileNewJoinFlow,
  getSwipeModeAccounts,
  getQuickOfferUsers,
  swipeModePassAccount,
  updateProfileOpeningOfferData,
  unlinkPhoneNumber,
};
